import AppConfig from '@/AppConfig';
import store from '@/store';
import axios from 'axios';

export default class EventService {
  static readonly VISITED_PRICING = 'visited_page_pricing';
  static readonly VISITED_CHECKOUT = 'visited_page_checkout';
  static readonly VISITED_PAGE_BILLING = 'visited_page_billing';
  // One name for every tracked control; which one was clicked travels in the
  // payload as cta_location, so a new surface needs no new event name.
  static readonly UI_CLICK = 'ui_click';

  static readonly CTA_BILLING_PAGE_UPGRADE = 'billing_page_upgrade_button';

  static readonly CTA_NAVBAR_LIMIT_UPGRADE = 'navbar_limit_reached_upgrade';

  static readonly CTA_NAVBAR_MENU_FEATURES_PRICING = 'navbar_menu_features_pricing';

  static readonly CTA_BILLING_PAGE_SELECT_PLAN = 'billing_page_select_plan_button';

  /** Marks a control as a tracked CTA; its value is the reported location. */
  static readonly CTA_ATTRIBUTE = 'data-cta-location';

  private static ctaClicksTracked = false;

  /**
   * Reports clicks on every control carrying CTA_ATTRIBUTE. Delegated because a
   * click on a Vuetify component with `to` never reaches an @click handler, and
   * capturing here runs before the navigation, lazily rendered or not.
   */
  public static trackCtaClicks(): void {
    // A second listener would report every click twice, which a module reload
    // re-running the app entry is enough to cause.
    if (this.ctaClicksTracked) {
      return;
    }

    this.ctaClicksTracked = true;

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target as Element | null;
        const ctaLocation = target?.closest?.(`[${this.CTA_ATTRIBUTE}]`)?.getAttribute(this.CTA_ATTRIBUTE);

        if (ctaLocation) {
          this.uiClick(ctaLocation);
        }
      },
      true
    );
  }

  /**
   * Reports a click on a tracked control.
   *
   * Always sent with keepalive, because every tracked control navigates away and
   * a normal request does not survive that. Measured on the billing page: the
   * same click sends nothing, but sends as soon as the navigation is prevented.
   *
   * The page is read here rather than by the API, which is on another origin and
   * so receives only our origin as the Referer.
   */
  public static uiClick(ctaLocation: string): void {
    this.createEventOnUnload(this.UI_CLICK, {
      cta_location: ctaLocation,
      page: window.location.pathname,
    });
  }

  /**
   * For events reported from a page the user stays on. A control that navigates
   * needs uiClick instead: this request is dropped when the navigation starts.
   */
  public static createEvent(eventName: string, payload: Record<string, unknown> = {}): void {
    axios
      .post(AppConfig.getAPIBaseUrl() + '/events', {
        event_name: eventName,
        payload: JSON.stringify(this.withAbTests(payload)),
      })
      .catch(() => {
        // Reporting must not surface as an unhandled rejection; a failure here
        // would otherwise be invisible except as noise in Sentry.
      });
  }

  /**
   * Reports an event from a control that navigates away. The browser drops a
   * normal request once the navigation starts, losing precisely the clicks worth
   * measuring; keepalive lets it finish regardless.
   *
   * Not sendBeacon, which cannot set the Authorization header the API requires.
   * The token comes from the store rather than the axios interceptor, so a tab
   * left idle past expiry loses the event rather than refreshing first -- an
   * acceptable trade for telemetry that must not delay a navigation.
   */
  public static createEventOnUnload(eventName: string, payload: Record<string, unknown> = {}): void {
    try {
      fetch(AppConfig.getAPIBaseUrl() + '/events', {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${store.state.auth.token}`,
        },
        body: JSON.stringify({
          event_name: eventName,
          payload: JSON.stringify(this.withAbTests(payload)),
        }),
      }).catch(() => {
        // Reporting a click must never interrupt the navigation it describes.
      });
    } catch {
      // Extensions patch fetch, and a patched one can throw rather than reject.
      // Letting that escape would break the click handler and block the click.
    }
  }

  /**
   * Adds the team's AB test assignments, appending to any the caller already set.
   */
  private static withAbTests(payload: Record<string, unknown>): Record<string, unknown> {
    if (!store.state.auth.team.ab_tests.length) {
      return payload;
    }

    const abTests = store.state.auth.team.ab_tests.join(',');

    return {
      ...payload,
      ab_tests: payload.ab_tests ? `${payload.ab_tests}, ${abTests}` : abTests,
    };
  }
}
